// src/contexts/AuthContext.jsx

import React, { createContext, useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { tokenStore } from "@/utils/tokenStore";
import axiosInstance, { resetRefreshFailure } from "@/utils/axiosInstance";
import {
  login as loginService,
  logout as logoutService,
  generateOTP as generateOTPService,
  verifyOtpService,
} from "@/services/authService";

import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import { setWorkspaceUser } from "@/utils/workspace";

const AuthContext = createContext(null);

const USER_KEY = "mobile_user";
const TOKEN_KEY = "mobile_access_token";
const REFRESH_KEY = "mobile_refresh_token";
const LOGO_KEY = "mobile_company_logo";

const isValidLogoUri = (v) =>
  typeof v === "string" &&
  v.startsWith("data:image/") &&
  v.indexOf(",") > 0 &&
  v.length - v.indexOf(",") > 200;

const cacheLogoFromSignedUrl = async (signedUrl) => {
  try {
    const resp = await CapacitorHttp.get({
      url: signedUrl,
      responseType: "blob",
    });
    if (!resp || resp.status >= 400 || !resp.data) return null;

    const dataUri = `data:image/png;base64,${resp.data}`;
    await Preferences.set({ key: LOGO_KEY, value: dataUri });
    return dataUri;
  } catch {
    return null;
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [twoFactorPending, setTwoFactorPending] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState(null);

  // Set during render, not in an effect: the pages' first requests (their
  // effects run before this provider's) must already be tagged correctly.
  setWorkspaceUser(user);

  const isMobile = Capacitor.getPlatform() !== "web";

  /* --------------------------------------------------
     REFRESH COMPANY LOGO FROM /me (mobile only)
  -------------------------------------------------- */
  const refreshCompanyLogoFromMe = async () => {
    if (!isMobile) return;
    try {
      const { data } = await axiosInstance.get("/auth/me", { workspace: "admin" });
      const signedUrl = data?.data?.user?.company_logo;

      if (!signedUrl) return;

      const dataUri = await cacheLogoFromSignedUrl(signedUrl);
      if (!dataUri) return;

      setUser((prev) => {
        if (!prev) return prev;
        if (prev.company_logo === dataUri) return prev;
        return { ...prev, company_logo: dataUri };
      });
    } catch {
      // silent — UI safety net handles visual fallback
    }
  };

  /* --------------------------------------------------
     INITIALIZE AUTH (AUTO LOGIN)
  -------------------------------------------------- */
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);

      try {
        if (isMobile) {
          const userResult = await Preferences.get({ key: USER_KEY });
          const tokenResult = await Preferences.get({ key: TOKEN_KEY });
          const logoResult = await Preferences.get({ key: LOGO_KEY });

          if (tokenResult.value) {
            tokenStore.set(tokenResult.value);
          }

          if (userResult.value) {
            const parsedUser = JSON.parse(userResult.value);
            if (isValidLogoUri(logoResult.value)) {
              parsedUser.company_logo = logoResult.value;
            }
            setUser(parsedUser);
            refreshCompanyLogoFromMe();
          }
        } else {
          const rawUser = Cookies.get("user");
          if (rawUser) {
            setUser(JSON.parse(rawUser));
          }
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /* --------------------------------------------------
     LOGIN
  -------------------------------------------------- */
  const login = async (credentials) => {
    setLoading(true);
    setError(null);

    try {
      const res = await loginService(credentials);
      resetRefreshFailure();

      if (res?.twoFactorRequired) {
        setTwoFactorPending(true);
        setTwoFactorUserId(res.userId);
        return {
          success: false,
          twoFactorRequired: true,
          userId: res.userId,
        };
      }

      tokenStore.set(res.access_token);
      if (isMobile) {
        await Preferences.set({
          key: TOKEN_KEY,
          value: res.access_token,
        });
        if (res.refresh_token) {
          await Preferences.set({ key: REFRESH_KEY, value: res.refresh_token });
        }
      }

      if (isMobile && res.user) {
        const dataUri = res.user.company_logo
          ? await cacheLogoFromSignedUrl(res.user.company_logo)
          : null;
        if (!dataUri) await Preferences.remove({ key: LOGO_KEY });
        res.user.company_logo = dataUri;
      }

      if (!res.subscription_gate?.blocked) {
        if (isMobile) {
          await Preferences.set({
            key: USER_KEY,
            value: JSON.stringify({ ...res.user, company_logo: undefined }),
          });
        } else {
          Cookies.set("user", JSON.stringify(res.user), { expires: 7 });
        }

        setUser(res.user);
      }
      setTwoFactorPending(false);
      setTwoFactorUserId(null);

      return {
        success: true,
        user: res.user,
        subscription_gate: res.subscription_gate,
      };
    } catch (err) {
      const msg = getErrorMessage(err, "Login failed");
      await clearSession();
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------------------------
     VERIFY OTP
  -------------------------------------------------- */
  const verifyOtp = async (userId, otp, isForgotPassword = false) => {
    setLoading(true);
    setError(null);

    try {
      const res = await verifyOtpService({ userId, otp, isForgotPassword, isMobile });
      resetRefreshFailure();

      tokenStore.set(res.access_token);
      if (isMobile) {
        await Preferences.set({
          key: TOKEN_KEY,
          value: res.access_token,
        });

        if (res.refresh_token) {
          await Preferences.set({ key: REFRESH_KEY, value: res.refresh_token });
        }
      }

      if (isMobile && res.user) {
        const dataUri = res.user.company_logo
          ? await cacheLogoFromSignedUrl(res.user.company_logo)
          : null;
        if (!dataUri) await Preferences.remove({ key: LOGO_KEY });
        res.user.company_logo = dataUri;
      }

      if (res.user) {
        if (isMobile) {
          await Preferences.set({
            key: USER_KEY,
            value: JSON.stringify({ ...res.user, company_logo: undefined }),
          });
        } else {
          Cookies.set("user", JSON.stringify(res.user), { expires: 7 });
        }

        setUser(res.user);
      }

      setTwoFactorPending(false);
      setTwoFactorUserId(null);

      return { success: true, user: res.user, resetToken: res.resetToken };
    } catch (err) {
      const msg = getErrorMessage(err, "Invalid or expired OTP");
      await clearSession();
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------------------------
     LOGOUT
  -------------------------------------------------- */
  const logout = async () => {
    await clearSession();
    try {
      await logoutService();
    } catch { }
  };

  /* --------------------------------------------------
     CLEAR SESSION (MOBILE + WEB SAFE)
  -------------------------------------------------- */
  const clearSession = async () => {
    tokenStore.clear();

    if (isMobile) {
      await Preferences.remove({ key: USER_KEY });
      await Preferences.remove({ key: TOKEN_KEY });
      await Preferences.remove({ key: REFRESH_KEY });
      await Preferences.remove({ key: LOGO_KEY });
    } else {
      Cookies.remove("user", { path: "/" });
      Cookies.remove("refreshToken");
    }

    setUser(null);
    setTwoFactorPending(false);
    setTwoFactorUserId(null);
  };

  /* --------------------------------------------------
     GENERATE OTP
  -------------------------------------------------- */
  const generateOTP = async (employeeId) => {
    try {
      return await generateOTPService(employeeId);
    } catch (err) {
      const msg = getErrorMessage(err, "Failed to generate OTP");
      setError(msg);
      throw new Error(msg);
    }
  };

  const clearError = () => setError(null);

  const refreshSession = async () => {
    try {
      await axiosInstance.get("/me");
    } catch (err) {
      if (isAuthExpiryError(err)) return;
      throw new Error(getErrorMessage(err, "Failed to refresh session"));
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        setError,
        twoFactorPending,
        twoFactorUserId,
        login,
        verifyOtp,
        logout,
        generateOTP,
        clearError,
        refreshSession,
        refreshCompanyLogo: refreshCompanyLogoFromMe,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
}
